import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CartesianGrid, LabelList, Scatter, ScatterChart, XAxis, YAxis, ZAxis } from 'recharts';

import { cn } from '@/lib/utils';
import { ChartContainer, ChartTooltip } from '@/components/ui/chart';
import { BarList } from '../../../components/design-system/analytics';
import { SectionCard, formatCompact, formatNumber } from '../../../components/design-system/admin';
import { MarketCompetitor, MarketData } from '../../../common/hooks/use-market-api.hook';
import { MapPoint, positioningPoints, sharedPageOneShare, sharedVisitShare, youOf } from '../../../modules/vendor/market/market-analysis';
import { competitorInsights, rivalReading } from '../../../modules/vendor/market/market-insights';
import { cleanDomain, rankedCompetitors } from '../../../modules/vendor/market/market-presentation';
import { CompareBars } from './compare-bars';
import { Nothing, OutLink, Reading, SortHead, TableFrame, Term, TermKey, sortRows, usd, useSort, useSpecText } from './market-shared';
import { Technologies } from './market-views';
import { UnlockRows } from '../../../components/design-system/plan/UnlockRows';

type SortKey = 'domain' | 'shared' | 'sharedTop10' | 'keywords' | 'visits' | 'top10' | 'value' | 'avg';

/**
 * Who the vendor is up against in search.
 *
 * A map first — every rival and the vendor on the same two axes, so "where
 * am I" is a glance — then who takes the visits on the searches you share,
 * the three closest head to head, and every rival in a table with one plain
 * line each. The big platforms are left out and named at the bottom.
 */
export const CompetitorsView: React.FC<{ data: MarketData }> = ({ data }) => {
    const { t } = useTranslation();
    const rivals = useMemo(() => rankedCompetitors(data), [data]);
    const excluded = data.competitors?.platformsExcluded ?? [];
    const specs = useMemo(() => competitorInsights(data), [data]);

    return (
        <div className="flex flex-col gap-6">
            {rivals.length === 0 ? (
                <Nothing title={t('MARKET.COMPETITORS.NONE')} description={t('MARKET.COMPETITORS.NONE_HINT')} />
            ) : (
                <>
                    <Reading specs={specs} />
                    <div className="grid gap-4 lg:grid-cols-5">
                        <PositioningMap data={data} className="lg:col-span-3" />
                        <SharedVisits data={data} className="lg:col-span-2" />
                    </div>
                    <Closest data={data} rivals={rivals.slice(0, 3)} />
                    <RivalTable data={data} rivals={rivals} />
                </>
            )}

            {/* What a site runs, beside who it competes with — the two answer one question. */}
            <Technologies data={data} />

            {excluded.length > 0 && (
                <p className="text-xs leading-relaxed text-muted-foreground">{t('MARKET.COMPETITORS.EXCLUDED', { list: excluded.join(', ') })}</p>
            )}
        </div>
    );
};

/** Powers of ten spanning the values, for a log axis whose ticks read as 10, 100, 1K. */
function logDomain(values: number[]): { domain: [number, number]; ticks: number[] } {
    const pos = values.filter((v) => v > 0);
    // Tight to the data: the decade below the smallest to the decade above the
    // largest. Starting at 1 whatever the data squashed every site into a corner.
    const lo = Math.pow(10, Math.floor(Math.log10(pos.length ? Math.min(...pos) : 1)));
    let hi = Math.pow(10, Math.ceil(Math.log10(pos.length ? Math.max(...pos) : 10)));
    if (hi <= lo) hi = lo * 10;
    const ticks: number[] = [];
    for (let v = lo; v <= hi; v *= 10) ticks.push(v);
    return { domain: [lo, hi], ticks };
}

const PositioningMap: React.FC<{ data: MarketData; className?: string }> = ({ data, className }) => {
    const { t } = useTranslation();
    const points = useMemo(() => positioningPoints(data), [data]);
    const labelled = useMemo(
        () =>
            new Set(
                points
                    .filter((p) => !p.self)
                    .sort((a, b) => b.shared - a.shared)
                    .slice(0, 3)
                    .map((p) => p.domain),
            ),
        [points],
    );
    // A log axis cannot place zero; one is the honest floor for "almost none".
    const plot = (p: MapPoint) => ({ ...p, x: Math.max(1, p.keywords), y: Math.max(1, p.visits), z: Math.max(1, p.shared), label: p.self || labelled.has(p.domain) ? p.domain : '' });
    const rivals = points.filter((p) => !p.self).map(plot);
    const self = points.filter((p) => p.self).map(plot);
    const x = logDomain([...rivals, ...self].map((p) => p.x));
    const y = logDomain([...rivals, ...self].map((p) => p.y));

    return (
        <SectionCard className={className} title={t('BUSINESS.COMPETITORS.MAP')} description={t('BUSINESS.COMPETITORS.MAP_HINT')} testId="business-positioning-map">
            {points.length < 2 ? (
                <p className="text-sm text-muted-foreground">{t('BUSINESS.COMPETITORS.MAP_EMPTY')}</p>
            ) : (
                <>
                    <ChartContainer config={{}} className="aspect-auto h-[320px] w-full" dir="ltr">
                        <ScatterChart margin={{ top: 16, right: 96, bottom: 16, left: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis
                                type="number"
                                dataKey="x"
                                scale="log"
                                domain={x.domain}
                                ticks={x.ticks}
                                tickFormatter={(v) => formatCompact(Number(v))}
                                tickLine={false}
                                axisLine={false}
                                tickMargin={8}
                                className="text-xs"
                                allowDataOverflow
                            />
                            <YAxis
                                type="number"
                                dataKey="y"
                                scale="log"
                                domain={y.domain}
                                ticks={y.ticks}
                                tickFormatter={(v) => formatCompact(Number(v))}
                                tickLine={false}
                                axisLine={false}
                                width={44}
                                className="text-xs"
                                allowDataOverflow
                            />
                            <ZAxis type="number" dataKey="z" range={[50, 650]} />
                            <ChartTooltip cursor={false} content={<MapTip />} />
                            <Scatter data={rivals} fill="var(--foreground)" fillOpacity={0.14} stroke="var(--foreground)" strokeOpacity={0.45} isAnimationActive={false}>
                                <LabelList dataKey="label" position="right" offset={10} className="fill-muted-foreground" style={{ fontSize: 10 }} />
                            </Scatter>
                            <Scatter data={self} fill="var(--foreground)" stroke="var(--background)" strokeWidth={2} isAnimationActive={false}>
                                {/* Below its dot, so it never sits on a rival's name. */}
                                <LabelList dataKey="label" position="bottom" offset={10} className="fill-foreground" style={{ fontSize: 11, fontWeight: 500 }} />
                            </Scatter>
                        </ScatterChart>
                    </ChartContainer>
                    <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-muted-foreground">
                        <span className="inline-flex items-center gap-1.5">
                            <span className="size-2.5 rounded-full bg-foreground" aria-hidden />
                            {t('BUSINESS.COMMON.YOU')}
                        </span>
                        <span className="inline-flex items-center gap-1.5">
                            <span className="size-2.5 rounded-full border border-foreground/45 bg-foreground/15" aria-hidden />
                            {t('BUSINESS.COMPETITORS.MAP_RIVALS')}
                        </span>
                        <span>{t('BUSINESS.COMPETITORS.MAP_AXES')}</span>
                    </div>
                </>
            )}
        </SectionCard>
    );
};

const MapTip: React.FC<{ active?: boolean; payload?: any[] }> = ({ active, payload }) => {
    const { t } = useTranslation();
    const p = payload?.[0]?.payload as (MapPoint & { x: number }) | undefined;
    if (!active || !p) return null;
    return (
        <div className="grid min-w-44 gap-1 rounded-lg border bg-background px-2.5 py-1.5 text-xs shadow-sm">
            <p className="font-medium">{p.self ? `${p.domain} · ${t('BUSINESS.COMMON.YOU')}` : p.domain}</p>
            <Line label={t('BUSINESS.COMPARE.KEYWORDS')} value={formatNumber(p.keywords)} />
            <Line label={t('BUSINESS.COMPARE.VISITS')} value={formatNumber(p.visits)} />
            {!p.self && <Line label={t('BUSINESS.COLUMN.SHARED')} value={formatNumber(p.shared)} />}
        </div>
    );
};

const Line: React.FC<{ label: string; value: string }> = ({ label, value }) => (
    <div className="flex items-baseline justify-between gap-4">
        <span className="text-muted-foreground">{label}</span>
        <span className="font-medium tabular-nums">{value}</span>
    </div>
);

/** Who takes the visits on the searches the vendor also appears for. */
const SharedVisits: React.FC<{ data: MarketData; className?: string }> = ({ data, className }) => {
    const { t } = useTranslation();
    const voice = useMemo(() => sharedVisitShare(data).slice(0, 8), [data]);
    const overlap = useMemo(() => rankedCompetitors(data).slice(0, 8), [data]);

    // Older snapshots have no per-rival shared visits: fall back to overlap alone.
    if (!voice.length) {
        return (
            <SectionCard className={className} title={<Term k="SHARED">{t('MARKET.COMPETITORS.OVERLAP')}</Term>} description={t('MARKET.COMPETITORS.OVERLAP_HINT')}>
                <BarList items={overlap.map((c) => ({ key: c.domain!, label: cleanDomain(c.domain), value: c.sharedKeywords ?? 0 }))} />
            </SectionCard>
        );
    }
    return (
        <SectionCard className={className} title={<Term k="SHARE_OF_VOICE">{t('BUSINESS.COMPETITORS.VOICE')}</Term>} description={t('BUSINESS.COMPETITORS.VOICE_HINT')}>
            <BarList
                items={voice.map((v) => ({
                    key: v.domain,
                    label: v.domain,
                    value: v.visits,
                    secondary: `${formatNumber(Math.round(v.share * 100))}%`,
                }))}
            />
        </SectionCard>
    );
};

/** The three rivals with the most searches in common, head to head. */
const Closest: React.FC<{ data: MarketData; rivals: MarketCompetitor[] }> = ({ data, rivals }) => {
    const { t } = useTranslation();
    const text = useSpecText();
    const you = youOf(data);
    return (
        <section className="flex flex-col gap-3" data-testid="business-closest">
            <div>
                <h2 className="text-sm font-medium">{t('BUSINESS.COMPETITORS.CLOSEST')}</h2>
                <p className="mt-0.5 text-xs text-muted-foreground">{t('BUSINESS.COMPETITORS.CLOSEST_HINT')}</p>
            </div>
            <div className="grid gap-4 md:grid-cols-3">
                {rivals.map((c) => {
                    const domain = cleanDomain(c.domain);
                    const reading = rivalReading(c, you);
                    return (
                        <div key={domain} className="flex min-w-0 flex-col gap-4 rounded-xl bg-card p-4 ring-1 ring-foreground/10">
                            <div className="min-w-0">
                                <OutLink href={domain} className="text-sm font-medium">
                                    {domain}
                                </OutLink>
                                <p className="mt-0.5 text-xs text-muted-foreground">
                                    {t('BUSINESS.COMPETITORS.IN_COMMON', { count: c.sharedKeywords ?? 0, n: formatNumber(c.sharedKeywords ?? 0) })}
                                </p>
                            </div>
                            <CompareBars
                                rival={domain}
                                rows={[
                                    { key: 'kw', label: t('BUSINESS.COMPARE.KEYWORDS'), you: you.keywords, them: c.organicKeywords },
                                    { key: 'visits', label: t('BUSINESS.COMPARE.VISITS'), you: you.visits, them: c.estimatedMonthlyVisits },
                                    { key: 'top10', label: t('BUSINESS.COMPARE.PAGE_ONE'), you: you.top10, them: c.top10 },
                                    {
                                        key: 'value',
                                        label: t('BUSINESS.COMPARE.VALUE'),
                                        you: you.trafficValue,
                                        them: c.trafficValue,
                                        format: (v) => usd(v),
                                    },
                                ]}
                            />
                            {reading.length > 0 && (
                                <ul className="mt-auto flex flex-col gap-1 border-t pt-3">
                                    {reading.map((m, i) => (
                                        <li key={i} className="text-xs leading-relaxed text-muted-foreground">
                                            {text(m)}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    );
                })}
            </div>
        </section>
    );
};

const RivalTable: React.FC<{ data: MarketData; rivals: MarketCompetitor[] }> = ({ data, rivals }) => {
    const { t } = useTranslation();
    const text = useSpecText();
    const you = youOf(data);
    const { sort, toggle } = useSort<SortKey>('shared');

    const rows = useMemo(() => {
        const get: Record<SortKey, (c: MarketCompetitor) => number | string | null | undefined> = {
            domain: (c) => cleanDomain(c.domain),
            shared: (c) => c.sharedKeywords,
            sharedTop10: (c) => sharedPageOneShare(c),
            keywords: (c) => c.organicKeywords,
            visits: (c) => c.estimatedMonthlyVisits,
            top10: (c) => c.top10,
            value: (c) => c.trafficValue,
            avg: (c) => c.avgPosition,
        };
        return sortRows(rivals, get[sort.key], sort.dir);
    }, [rivals, sort]);

    const head = (key: SortKey, label: string, opts: { term?: TermKey; first?: 'asc' | 'desc'; align?: 'start' | 'end' } = {}) => (
        <SortHead
            label={label}
            term={opts.term}
            align={opts.align}
            active={sort.key === key}
            dir={sort.dir}
            onClick={() => toggle(key, opts.first ?? 'desc')}
        />
    );

    return (
        <SectionCard flush title={t('BUSINESS.COMPETITORS.TABLE')} description={t('BUSINESS.COMPETITORS.TABLE_HINT')} testId="business-rivals">
            <TableFrame className="border-t">
                <thead className="border-b text-xs text-muted-foreground">
                    <tr>
                        {head('domain', t('BUSINESS.COLUMN.COMPETITOR'), { align: 'start', first: 'asc' })}
                        {head('shared', t('BUSINESS.COLUMN.SHARED'), { term: 'SHARED' })}
                        {head('sharedTop10', t('BUSINESS.COLUMN.SHARED_TOP10'))}
                        {head('keywords', t('BUSINESS.COMPARE.KEYWORDS'))}
                        {head('visits', t('BUSINESS.COMPARE.VISITS'))}
                        {head('top10', t('BUSINESS.COMPARE.PAGE_ONE'))}
                        {head('value', t('BUSINESS.COMPARE.VALUE'), { term: 'TRAFFIC_VALUE' })}
                        {head('avg', t('BUSINESS.COLUMN.AVG_POSITION'), { first: 'asc' })}
                    </tr>
                </thead>
                <tbody>
                    <tr className="border-b bg-muted/40">
                        <td className="p-3 font-medium">
                            {you.domain} <span className="text-xs font-normal text-muted-foreground">· {t('BUSINESS.COMMON.YOU')}</span>
                        </td>
                        <td className="p-3 text-end text-muted-foreground">—</td>
                        <td className="p-3 text-end text-muted-foreground">—</td>
                        <td className="p-3 text-end font-medium tabular-nums">{formatNumber(you.keywords)}</td>
                        <td className="p-3 text-end font-medium tabular-nums">{formatNumber(you.visits)}</td>
                        <td className="p-3 text-end font-medium tabular-nums">{formatNumber(you.top10)}</td>
                        <td className="p-3 text-end font-medium tabular-nums">{you.trafficValue != null ? usd(you.trafficValue) : '—'}</td>
                        <td className="p-3 text-end text-muted-foreground">—</td>
                    </tr>
                    {rows.map((c) => {
                        const domain = cleanDomain(c.domain);
                        const s = sharedPageOneShare(c);
                        const first = rivalReading(c, you)[0];
                        return (
                            <tr key={domain} className="border-b last:border-b-0 hover:bg-muted/30">
                                <td className="max-w-[260px] p-3 align-top">
                                    <OutLink href={domain} className="font-medium">
                                        {domain}
                                    </OutLink>
                                    {first && <p className="mt-0.5 truncate text-xs text-muted-foreground">{text(first)}</p>}
                                </td>
                                <td className="p-3 text-end align-top tabular-nums">{formatNumber(c.sharedKeywords)}</td>
                                <td className="p-3 text-end align-top tabular-nums">
                                    {c.sharedTop10 != null ? (
                                        <>
                                            {formatNumber(c.sharedTop10)}
                                            {s != null && <span className="ms-1.5 text-xs text-muted-foreground">{formatNumber(Math.round(s * 100))}%</span>}
                                        </>
                                    ) : (
                                        '—'
                                    )}
                                </td>
                                <Num value={c.organicKeywords} mine={you.keywords} />
                                <Num value={c.estimatedMonthlyVisits} mine={you.visits} />
                                <Num value={c.top10} mine={you.top10} />
                                <td className="p-3 text-end align-top tabular-nums">{c.trafficValue != null ? usd(c.trafficValue) : '—'}</td>
                                <td className="p-3 text-end align-top tabular-nums">{c.avgPosition != null ? formatNumber(c.avgPosition, 1) : '—'}</td>
                            </tr>
                        );
                    })}
                </tbody>
            </TableFrame>
            <UnlockRows count={data.locked?.competitors} noun="competitors" reason="maxCompetitorRows" testId="unlock-competitors" />
        </SectionCard>
    );
};

/** A rival's figure, darker when it beats yours: the table reads as a scoreboard. */
const Num: React.FC<{ value: number | undefined; mine: number }> = ({ value, mine }) => (
    <td className={cn('p-3 text-end align-top tabular-nums', value != null && value > mine ? 'text-foreground' : 'text-muted-foreground')}>
        {value != null ? formatNumber(value) : '—'}
    </td>
);
